<template>
  <div class="page">
    <!-- Sticky Header -->
    <header class="header">
      <div class="container">

        <ToastManagerVue ref="toastManager" />


        <button class="back-btn" @click="handleBack">
          ← Back
        </button>

        <h1>
          {{ currentStep === 1 ? "Self Registration" : "Review Your Information" }}
        </h1>

        <!-- Progress -->
        <div class="progress">
          <div class="step">
            <span class="circle">1</span>
            <span class="label">Step 1</span>
          </div>

          <div class="line" :class="{ 'line-pink': currentStep === 2 }"></div>


          <div class="step">
            <span class="circle" :class="{ inactive: currentStep === 1 }">
              2
            </span>
            <span class="label" :class="{ muted: currentStep === 1 }">
              Step 2
            </span>
          </div>
        </div>
      </div>
    </header>

    <!-- Content -->
    <main class="content">
      <div class="container">

        <!-- STEP 1 -->
        <form v-if="currentStep === 1" class="form" @submit.prevent="submitForm">
          <div class="field">
            <label>Full Name <span>*</span></label>
            <input v-model="form.full_name" type="text" placeholder="Enter your full name" />
            <small v-if="errors.fullName" class="error">
              <span class="error-icon">⚠</span>{{ errors.fullName }}
            </small>
          </div>

          <div class="field">
            <label>State <span>*</span></label>
            <select v-model="form.state">
              <option value="">Select your state</option>
              <option v-for="state in states" :key="state.alias" :value="state.state">
                {{ state.state }}
              </option>
            </select>
            <small v-if="errors.state" class="error">
              <span class="error-icon">⚠</span>{{ errors.state }}
            </small>
          </div>

          <div class="field">
            <label>LGA <span>*</span></label>
            <select v-model="form.lga" :disabled="!form.state">
              <option value="">
                {{ form.state ? "Select LGA" : "Please select a state first" }}
              </option>
              <option v-for="lga in lgas" :key="lga" :value="lga">
                {{ lga }}
              </option>
            </select>
            <small v-if="errors.lga" class="error">
              <span class="error-icon">⚠</span>{{ errors.lga }}
            </small>
          </div>

          <div class="field">
            <label>Phone Number <span>*</span></label>
            <div class="phone-input">
              <span>+234</span>
              <input v-model="form.phone_number" type="tel" maxlength="10" placeholder="8012345678" />
            </div>
            <small v-if="errors.phone" class="error">
              <span class="error-icon">⚠</span>{{ errors.phone }}
            </small>
          </div>

          <div class="field">
            <label>WhatsApp Number <span>*</span></label>
            <div class="phone-input">
              <span>+234</span>
              <input v-model="form.whatsapp_number" type="tel" maxlength="10" placeholder="8012345678" />
            </div>
            <small v-if="errors.whatsapp" class="error">
              <span class="error-icon">⚠</span>{{ errors.whatsapp }}
            </small>
          </div>

          <div class="checkbox">
            <input id="sameAsPhone" type="checkbox" v-model="sameAsPhone" @change="copyPhone" />
            <label for="sameAsPhone">Same as phone number</label>
          </div>

          <button class="submit">Continue</button>

          <div class="help">
            <button type="button">Need help?</button>
          </div>
        </form>

        <!-- STEP 2 -->
        <div v-if="currentStep === 2">
          <div class="form">
            <div class="group-form-edit">
              <h3>Personal Information</h3>
              <button class="edit-btn" @click="currentStep = 1">
                <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none"
                  stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"
                  class="pen-icon">
                  <path
                    d="M21.174 6.812a1 1 0 0 0-3.986-3.987L3.842 16.174a2 2 0 0 0-.5.83l-1.321 4.352a.5.5 0 0 0 .623.622l4.353-1.32a2 2 0 0 0 .83-.497z">
                  </path>
                </svg>
                Edit
              </button>
            </div>


            <div class="review">
              <span>Full Name</span><span>{{ form.full_name }}</span>
              <span>State</span><span>{{ form.state }}</span>
              <span>LGA</span><span>{{ form.lga }}</span>
              <span>Phone</span><span>+234{{ form.phone_number }}</span>
              <span>WhatsApp</span><span>+234{{ form.whatsapp_number }}</span>
            </div>



          </div>

          <div class="form">
            <div class="checkbox">
              <input type="checkbox" v-model="termsAccepted" />
              <label>
                I agree to the <router-link :to="{ name: 'CGTerms' }" class="link">
                  terms and conditions
                </router-link> and confirm that the information provided
                is
                accurate
              </label>
            </div>

            <small v-if="errors.terms" class="error">
              <span class="error-icon">⚠</span>{{ errors.terms }}
            </small>

          </div>

          <button class="submit" @click="finalSubmit">
            Complete Registration
          </button>

          <button class="custom-btn" @click="currentStep = 1">
            Go Back
          </button>

        </div>

      </div>
    </main>
  </div>
</template>

<script>
import statesData from "@/data/states.js";
import Request from "@/utils/apiclient.js";
import { formatErrorMessages } from "@/utils/errorHandler";
import ToastManagerVue from "@/components/ToastAlert/ToastManager.vue";

export default {
  name: "SelfRegistration",
  components: {
    ToastManagerVue
  },
  data() {
    return {
      currentStep: 1,
      sameAsPhone: false,
      termsAccepted: false,
      form: {
        full_name: "",
        state: "",
        lga: "",
        phone_number: "",
        whatsapp_number: "",
      },
      isSubmitting: false,
      errors: {},
      states: statesData.states,
      lgas: [],
    };
  },
  watch: {
    "form.state"(state) {
      this.form.lga = "";
      const selected = this.states.find(s => s.state === state);
      this.lgas = selected ? selected.lgas : [];
    },
  },
  methods: {
    copyPhone() {
      if (this.sameAsPhone) {
        this.form.whatsapp_number = this.form.phone_number;
      }
    },

    showToast(msg, statusValue) {
      this.$refs.toastManager.addToast(msg, statusValue);
      return true;
    },

    handleBack() {
      if (this.currentStep === 1) {
        // Redirect to landing page
        this.$router.push({ name: 'CgWorkshop' }); // Replace "/" with your landing page route
      } else {
        // Go back to previous step
        this.currentStep = 1;
      }
    },

    submitForm() {
      this.errors = {};

      if (!this.form.full_name) this.errors.fullName = "Full name is required";
      if (!this.form.state) this.errors.state = "State is required";
      if (!this.form.lga) this.errors.lga = "LGA is required";
      if (!this.form.phone_number) this.errors.phone = "Phone is required";
      if (!this.form.whatsapp_number) this.errors.whatsapp = "WhatsApp is required";

      if (Object.keys(this.errors).length === 0) {
        this.currentStep = 2;
      }
    },
    finalSubmit() {
      this.errors = {};
      if (!this.termsAccepted) {
        this.errors.terms = "You must accept the terms";
        return;
      }

      // Clone form and prepend +234 to phone numbers
      const payload = {
        ...this.form,
        registration_type: "self_registration",
        phone_number: "+234" + this.form.phone_number,
        whatsapp_number: "+234" + this.form.whatsapp_number,
      };

      const request = new Request("/workshop/registrations/");
      request.post(payload, (data, error) => {
        this.isSubmitting = true;
        console.log(payload);
        if (error) {
          this.isSubmitting = false;
          console.log("data error", error);
          if (error.status == "failure") {
            return this.showToast(error.message, "error");
          }
          if (error.detail) {
            return this.showToast("An error occurred", "error");
          }
          const errorMessages = formatErrorMessages(error);
          console.log("error", errorMessages);
          errorMessages.forEach((message) => this.showToast(message, "error"));
        } else {
          console.log("data success here", data);
          this.currentStep = 1;
          this.form = { full_name: "", state: "", lga: "", phone_number: "", whatsapp_number: "" };

          this.isSubmitting = false;
          this.$router.push({
            name: "CgWorkshopConfirm",
            state: {
              registrationType: data.registration_type,
              registrationNumber: data.registration_number,
              participantName: data.full_name,
              registrationDate: data.created_at,
            },
          });
        }
      });

    },
  },
};
</script>

<style scoped>
/* Base */
.page {
  min-height: 100vh;
  background: #f8f9fa;
  font-family: system-ui, -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif;
  color: #212529;
}

.container {
  max-width: 640px;
  margin: auto;
  padding: 0 16px;
}

/* Header */
.header {
  background: #ffffff;
  border-bottom: 1px solid #ced4da;
  position: sticky;
  top: 0;
  z-index: 10;
  padding: 16px 0 12px;
  margin-top: 20px;
}

.header h1 {
  font-size: 1.25rem;
  font-weight: 600;
  margin-bottom: 12px;
}

.back-btn {
  background: none;
  border: none;
  color: #f59cab;
  cursor: pointer;
  font-size: 0.9rem;
  padding: 0;
  margin: 10px 0;
}

.back-btn:hover {
  text-decoration: underline;
}

/* Progress */
.progress {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 14px;
}

.step {
  display: flex;
  align-items: center;
  gap: 6px;
  padding: 20px 0 20px;
}

.circle {
  width: 32px;
  height: 32px;
  border-radius: 50%;
  background: #f59cab;
  color: #ffffff;
  font-size: 0.85rem;
  font-weight: 600;
  display: flex;
  align-items: center;
  justify-content: center;
}

.circle.inactive {
  background: #e9ecef;
  color: #6c757d;
}

.label {
  font-size: 0.85rem;
  font-weight: 500;
}

.muted {
  color: #6c757d;
}

.line {
  width: 48px;
  height: 2px;
  background: #ced4da;
}

.line-pink {
  background: #f59cab;
  /* pink color */
}

/* Content */
.content {
  padding: 28px 0;
}

/* Card */
.form {
  background: #ffffff;
  padding: 28px;
  border-radius: 18px;
  box-shadow: 0 12px 28px rgba(0, 0, 0, 0.08);
  margin-bottom: 20px;
}

.form h3 {
  font-size: 1.05rem;
  font-weight: 600;
  margin-bottom: 16px;
}

/* Fields */
.field {
  margin-bottom: 20px;
}

label {
  font-size: 0.85rem;
  font-weight: 500;
}

label span {
  color: #dc3545;
}

input,
select {
  width: 100%;
  height: 42px;
  margin-top: 6px;
  border-radius: 8px;
  border: 1px solid #ced4da;
  padding: 0 14px;
  font-size: 0.9rem;
}

input:focus,
select:focus {
  outline: none;
  border-color: #f59cab;
  box-shadow: 0 0 0 3px rgba(245, 156, 171, 0.15);
}

/* Phone */
.phone-input {
  position: relative;
}

.phone-input span {
  position: absolute;
  left: 14px;
  top: 55%;
  transform: translateY(-50%);
  font-size: 0.85rem;
  color: #6c757d;
}

.phone-input input {
  padding-left: 56px;
}

/* Checkbox */
.checkbox {
  display: flex;
  align-items: center;
  gap: 10px;
  margin-top: 14px;
  font-size: 0.85rem;
}

input[type="checkbox"] {
  width: 16px;
  height: 16px;
  accent-color: #f59cab;
  cursor: pointer;
}

/* Review */
.review {
  display: grid;
  grid-template-columns: 1fr 2fr;
  gap: 10px 8px;
  margin-bottom: 20px;
}

.review span:nth-child(odd) {
  color: #6c757d;
}

/* Button */
.submit {
  width: 100%;
  height: 48px;
  background: #f59cab;
  color: #ffffff;
  border: none;
  border-radius: 10px;
  margin-top: 18px;
  font-size: 0.95rem;
  font-weight: 600;
  cursor: pointer;
}

.submit:hover {
  opacity: 0.95;
}

/* Help */
.help {
  display: flex;
  justify-content: center;
  margin-top: 22px;
}

.help button {
  background: none;
  border: none;
  color: #f59cab;
  font-size: 0.85rem;
  cursor: pointer;
}

.help button:hover {
  text-decoration: underline;
}

/* Error */
.error {
  display: block;
  margin-top: 6px;
  color: #dc3545;
  font-size: 0.75rem;
}

a {
  color: #f59cab;
}

.custom-btn {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  width: 100%;
  height: 48px;
  padding: 0 16px;
  font-size: 0.9rem;
  font-weight: 500;
  border-radius: 8px;
  border: 1px solid #f59cab;
  background-color: #ffffff;
  color: #f59cab;
  cursor: pointer;
  transition: all 0.2s ease-in-out;
  text-align: center;
  margin-top: 20px;
}

.group-form-edit {
  display: flex;
  justify-content: space-between;
}

.edit-btn {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  /* space between icon and text */
  padding: 4px 8px;
  background: none;
  border: none;
  color: #f59cab;
  /* primary color */
  cursor: pointer;
  font-size: 0.85rem;
}

.edit-btn:hover {
  text-decoration: underline;
}

.pen-icon {
  width: 16px;
  height: 16px;
  flex-shrink: 0;
}
</style>
